import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { 
  Scale, X, Trophy, IndianRupee, MapPin, Building2, 
  GraduationCap, TrendingUp, Users, Star, Check, Minus
} from 'lucide-react';

// Floating comparison bar component
export const ComparisonBar = ({ selectedColleges, onRemove, onCompare, onClear }) => {
  if (selectedColleges.length === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-blue-500 shadow-lg z-50 animate-slide-up">
      <div className="max-w-6xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-blue-600">
              <Scale className="w-5 h-5" />
              <span className="font-semibold">Compare ({selectedColleges.length}/3)</span>
            </div>
            <div className="flex gap-2">
              {selectedColleges.map(college => (
                <div 
                  key={college.id} 
                  className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full"
                >
                  <span className="text-sm font-medium text-blue-800 max-w-[150px] truncate">
                    {college.name}
                  </span>
                  <button 
                    onClick={() => onRemove(college.id)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClear}>
              Clear All
            </Button>
            <Button 
              size="sm" 
              onClick={onCompare}
              disabled={selectedColleges.length < 2}
            >
              <Scale className="w-4 h-4 mr-2" />
              Compare Now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main comparison modal
const CollegeComparison = ({ colleges, isOpen, onClose }) => {
  if (!colleges || colleges.length < 2) return null;

  // Helper to get fee value
  const getFee = (college) => {
    const fees = college.fees || {};
    if (typeof fees === 'object') {
      return { min: fees.min || 0, max: fees.max || 0 };
    }
    return { min: fees || 0, max: fees || 0 };
  };

  // Helper to format currency
  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Get best value for comparison (lower is better for fees, rank; higher is better for others)
  const getBestValue = (values, lowerIsBetter = true) => {
    const validValues = values.filter(v => v !== null && v !== undefined && v !== 0);
    if (validValues.length === 0) return null;
    return lowerIsBetter ? Math.min(...validValues) : Math.max(...validValues);
  };

  // Comparison parameters
  const parameters = [
    {
      label: 'College Type',
      icon: Building2,
      getValue: (c) => c.type,
      format: (v) => v || 'N/A',
      compare: false
    },
    {
      label: 'Location',
      icon: MapPin,
      getValue: (c) => `${c.city}, ${c.state}`,
      format: (v) => v || 'N/A',
      compare: false
    },
    {
      label: 'NIRF Rank',
      icon: Trophy,
      getValue: (c) => c.nirf_rank,
      format: (v) => v ? `#${v}` : 'Not Ranked',
      compare: true,
      lowerIsBetter: true
    },
    {
      label: 'NAAC Grade',
      icon: Star,
      getValue: (c) => c.naac_grade,
      format: (v) => v || 'N/A',
      compare: true,
      lowerIsBetter: false,
      customBest: (values) => {
        const grades = ['A++', 'A+', 'A', 'B++', 'B+', 'B', 'C'];
        for (const grade of grades) {
          if (values.includes(grade)) return grade;
        }
        return null;
      }
    },
    {
      label: 'Annual Fees (Min)',
      icon: IndianRupee,
      getValue: (c) => getFee(c).min,
      format: formatCurrency,
      compare: true,
      lowerIsBetter: true
    },
    {
      label: 'Annual Fees (Max)',
      icon: IndianRupee,
      getValue: (c) => getFee(c).max,
      format: formatCurrency,
      compare: true,
      lowerIsBetter: true
    },
    {
      label: 'Hostel Fees',
      icon: IndianRupee,
      getValue: (c) => c.hostelFees,
      format: formatCurrency,
      compare: true,
      lowerIsBetter: true
    },
    {
      label: '4-Year Total Cost',
      icon: IndianRupee,
      getValue: (c) => {
        const fee = getFee(c);
        const hostel = c.hostelFees || 30000;
        const mess = c.messFees || 40000;
        return (fee.min + hostel + mess) * 4;
      },
      format: formatCurrency,
      compare: true,
      lowerIsBetter: true
    },
    {
      label: 'Established',
      icon: Building2,
      getValue: (c) => c.established,
      format: (v) => v ? `Est. ${v}` : 'N/A',
      compare: true,
      lowerIsBetter: true // Older is often better
    },
    {
      label: 'Branches Offered',
      icon: GraduationCap,
      getValue: (c) => c.branches?.length,
      format: (v) => v ? `${v} branches` : 'N/A',
      compare: true,
      lowerIsBetter: false
    }
  ];

  // Get JEE cutoff (General category, 2024)
  const getCutoff = (college) => {
    if (!college.branches || college.branches.length === 0) return null;
    
    // Get CSE branch cutoff if available
    const cseBranch = college.branches.find(b => 
      b.name?.toLowerCase().includes('computer') || b.code === 'CSE'
    );
    const branch = cseBranch || college.branches[0];
    
    const cutoffs = branch?.cutoffs?.jeeMain?.['2024']?.general;
    return cutoffs ? cutoffs.closing : null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Scale className="w-6 h-6 text-blue-600" />
            College Comparison
          </DialogTitle>
          <DialogDescription>
            Side-by-side comparison of {colleges.length} colleges
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* College Headers */}
          <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: `200px repeat(${colleges.length}, 1fr)` }}>
            <div></div>
            {colleges.map((college, idx) => (
              <Card key={college.id} className={`${idx === 0 ? 'border-blue-500 border-2' : ''}`}>
                <CardContent className="p-4 text-center">
                  <Badge className="mb-2" variant={
                    college.type === 'IIT' ? 'default' :
                    college.type === 'NIT' ? 'secondary' :
                    'outline'
                  }>
                    {college.type}
                  </Badge>
                  <h3 className="font-semibold text-slate-800 text-sm leading-tight mb-1">
                    {college.name}
                  </h3>
                  <p className="text-xs text-slate-500">{college.city}, {college.state}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Comparison Table */}
          <div className="space-y-1">
            {parameters.map((param, paramIdx) => {
              const values = colleges.map(c => param.getValue(c));
              let bestValue = null;
              
              if (param.compare) {
                if (param.customBest) {
                  bestValue = param.customBest(values);
                } else {
                  bestValue = getBestValue(values, param.lowerIsBetter);
                }
              }

              return (
                <div 
                  key={paramIdx}
                  className={`grid gap-4 py-3 px-4 rounded-lg ${paramIdx % 2 === 0 ? 'bg-slate-50' : ''}`}
                  style={{ gridTemplateColumns: `200px repeat(${colleges.length}, 1fr)` }}
                >
                  {/* Parameter Label */}
                  <div className="flex items-center gap-2 text-slate-700 font-medium">
                    <param.icon className="w-4 h-4 text-slate-500" />
                    <span className="text-sm">{param.label}</span>
                  </div>
                  
                  {/* Values */}
                  {colleges.map((college, colIdx) => {
                    const value = param.getValue(college);
                    const isBest = param.compare && bestValue !== null && (
                      param.customBest 
                        ? value === bestValue 
                        : value === bestValue
                    );
                    
                    return (
                      <div 
                        key={colIdx}
                        className={`text-center py-1 px-2 rounded ${
                          isBest ? 'bg-green-100 text-green-800 font-semibold' : 'text-slate-700'
                        }`}
                      >
                        <span className="text-sm flex items-center justify-center gap-1">
                          {param.format(value)}
                          {isBest && <Check className="w-4 h-4 text-green-600" />}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* JEE Cutoff Row */}
            <div 
              className="grid gap-4 py-3 px-4 rounded-lg bg-blue-50"
              style={{ gridTemplateColumns: `200px repeat(${colleges.length}, 1fr)` }}
            >
              <div className="flex items-center gap-2 text-slate-700 font-medium">
                <TrendingUp className="w-4 h-4 text-slate-500" />
                <span className="text-sm">JEE Main Cutoff (2024)</span>
              </div>
              {colleges.map((college, colIdx) => {
                const cutoff = getCutoff(college);
                const allCutoffs = colleges.map(getCutoff).filter(c => c !== null);
                const bestCutoff = allCutoffs.length > 0 ? Math.min(...allCutoffs) : null;
                const isBest = cutoff && cutoff === bestCutoff;
                
                return (
                  <div 
                    key={colIdx}
                    className={`text-center py-1 px-2 rounded ${
                      isBest ? 'bg-green-100 text-green-800 font-semibold' : 'text-slate-700'
                    }`}
                  >
                    <span className="text-sm flex items-center justify-center gap-1">
                      {cutoff ? `Rank ${cutoff.toLocaleString()}` : 'N/A'}
                      {isBest && <Check className="w-4 h-4 text-green-600" />}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="mt-6 flex items-center gap-4 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-green-100 rounded flex items-center justify-center">
                <Check className="w-3 h-3 text-green-600" />
              </div>
              <span>Best value in category</span>
            </div>
            <span>•</span>
            <span>Lower fees & cutoff ranks are better</span>
            <span>•</span>
            <span>Higher NIRF rank means lower number (better)</span>
          </div>

          {/* Summary */}
          <Card className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-slate-800 mb-2">Quick Summary</h4>
              <div className="grid grid-cols-3 gap-4">
                {colleges.map((college, idx) => {
                  const fee = getFee(college);
                  const cutoff = getCutoff(college);
                  
                  return (
                    <div key={idx} className="bg-white p-3 rounded-lg">
                      <p className="font-medium text-sm text-slate-800 truncate">{college.name}</p>
                      <div className="mt-2 space-y-1 text-xs text-slate-600">
                        <p>💰 {formatCurrency(fee.min)}/year</p>
                        <p>🎯 Cutoff: {cutoff ? `Rank ${cutoff.toLocaleString()}` : 'N/A'}</p>
                        <p>🏆 NIRF: {college.nirf_rank ? `#${college.nirf_rank}` : 'N/A'}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CollegeComparison;
